import { faFilePen, faPenToSquare } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRouteLayer } from "../../../../../hooks/useCurrentEntities";
import { FileType, ReportFolder } from "../../../../../constants/misc";
import { useAddReportMutation } from "../../../../../hooks/mutations/useLayerMutations";
import usePatchLayer from "../../../../../hooks/usePatchLayer";
import useAuthorization from "../../../../../utils/authorization";
import { blobToBase64 } from "../../../../../utils/base64";
import useNavigateFrom from "../../../../../utils/useNavigateFrom";
import { Button } from "../../../../Helper/Button";

export const QC_STATUS = {
    UNREADY: "",
    DRAFT: "DRAFT",
    SENT: "SENT",
    READY: "READY"
};

type QCStatusButtonProps = {
    pdfConfig: any;
    pdfBlob?: Blob | null;
    configValue: string;
    draftReportKey: string;
    qcStatusField: string;
    backUrl?: string;
    editUrl?: string;
};

export default function QCStatusButton({ pdfConfig, pdfBlob, configValue, draftReportKey, qcStatusField, backUrl, editUrl, ...buttonProps }: QCStatusButtonProps) {
    const patchLayer = usePatchLayer();
    const addReportMutation = useAddReportMutation();
    const layer = useRouteLayer();
    const auth = useAuthorization();
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const { navigateBack } = useNavigateFrom();
    const navigate = useNavigate();

    const onSaveDraftReport = async () => {
        if (layer?.id && pdfBlob) {
            setIsSavingDraft(true); // Disable button
            try {
                const base64Data = await blobToBase64(pdfBlob);
                await addReportMutation.mutateAsync({
                    configValue,
                    file: { name: "generated", content: base64Data, type: FileType.PDF },
                    layerId: layer.id,
                    folder: ReportFolder.EYE,
                });
                await patchLayer(layer, qcStatusField, QC_STATUS.DRAFT, true);

                if (backUrl) {
                    navigateBack(backUrl);
                }
            } finally {
                setIsSavingDraft(false); // Re-enable button regardless of success or failure
            }
        }
    };

    const onEditReport = async () => {
        await patchLayer(layer, qcStatusField, QC_STATUS.UNREADY, true);
        await patchLayer(layer, draftReportKey, "", true);

        if (editUrl) {
            navigate(editUrl);
        }
    };

    if (!pdfConfig || !auth.userBelongsToOneOfTeams(pdfConfig.teams_that_can_manage_qc_status)) {
        return null;
    }

    return (
        <>
            {layer[qcStatusField] === QC_STATUS.DRAFT || layer[qcStatusField] === QC_STATUS.SENT ? (
                <Button color="warning" icon={faPenToSquare} onClick={onEditReport} iconOnlyBreakpoint="md">{pdfConfig?.text_edit_report || "Edit"}</Button>

            ) : (
                <Button color="primary" outline icon={faFilePen} disabled={isSavingDraft} onClick={onSaveDraftReport} {...buttonProps} iconOnlyBreakpoint="md">{pdfConfig?.text_save_draft_report || "Save draft report"}</Button>
            )}
        </>
    );
}
